import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  showService, 
  InternalDetailsData, 
  Promo, 
  DubbingProjectResponse,
  DubbingProjectCreateDTO,
  DubbingProjectUpdateDTO,
  PromoUpdateDTO
} from '../services/showService';

// Hook for Internal Details
export const useInternalDetails = (showId: string | null) => {
  const [data, setData] = useState<InternalDetailsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!showId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await showService.getInternalDetails(showId);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch internal details';
      setError(errorMessage);
      console.error('Error fetching internal details:', err);
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const updateData = useCallback(async (updatedData: InternalDetailsData) => {
    if (!showId) return;
    
    try {
      setLoading(true);
      const result = await showService.updateInternalDetails(showId, updatedData);
      setData(result);
      toast.success('Internal details updated successfully');
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update internal details';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, updateData, refetch: fetchData };
};

// Hook for Promo Details
export const usePromoDetails = (showId: string | null) => {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [successfulLinks, setSuccessfulLinks] = useState<string[]>([]);
  const [promoPerformanceDetails, setPromoPerformanceDetails] = useState<string>('');
  const [outsourcedShow, setOutsourcedShow] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPromos = useCallback(async () => {
    if (!showId) return;

    setLoading(true);
    setError(null);

    try {
      const [promosResult, showDataResult] = await Promise.all([
        showService.getPromosForShow(showId),
        showService.checkAndFetchOutsourcedShow(showId)
      ]);

      console.log('Promos result:', promosResult);
      console.log('Show data result:', showDataResult);

      // Extract metadata from main show
      const { mainShow, outsourcedShow } = showDataResult;

      // Combine main show promos and outsourced show promos
      let combinedPromos = [...promosResult];

      // Add outsourced show promos if they exist
      if (outsourcedShow && outsourcedShow.promos && Array.isArray(outsourcedShow.promos)) {
        const outsourcedPromos = outsourcedShow.promos.map((promo: any) => ({
          ...promo,
          isOutsourced: true,
          showName: outsourcedShow.name || 'Outsourced Show'
        }));
        combinedPromos = [...combinedPromos, ...outsourcedPromos];
      }

      setPromos(combinedPromos);

      const successfulLinksMetadata = mainShow.metadata?.find(m => m.type === 'SUCCESSFUL_PROMOS');
      const performanceDetailsMetadata = mainShow.metadata?.find(m => m.type === 'PROMO_PERFORMANCE_DETAILS');

      setSuccessfulLinks(successfulLinksMetadata?.value ? JSON.parse(successfulLinksMetadata.value) : []);
      setPromoPerformanceDetails(performanceDetailsMetadata?.value || '');

      // Set outsourced show data if it exists
      setOutsourcedShow(outsourcedShow || null);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch promo details';
      setError(errorMessage);
      console.error('Error fetching promo details:', err);
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const addPromo = useCallback(async (promo: Promo) => {
    if (!showId) return;

    try {
      setLoading(true);
      const result = await showService.addPromoToShow(showId, promo);
      setPromos(prev => [...prev, result]);
      toast.success('Promo added successfully');
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add promo';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const updatePromo = useCallback(async (promoId: string, updateData: PromoUpdateDTO) => {
    if (!showId) return;

    try {
      setLoading(true);
      const result = await showService.updatePromoForShow(showId, promoId, updateData);
      setPromos(prev => prev.map(p => p.id === promoId ? result : p));
      toast.success('Promo updated successfully');
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update promo';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const deletePromo = useCallback(async (promoId: string) => {
    if (!showId) return;

    try {
      setLoading(true);
      await showService.deletePromoFromShow(showId, promoId);
      setPromos(prev => prev.filter(p => p.id !== promoId));
      toast.success('Promo deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete promo';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const updateSuccessfulLinks = useCallback(async (links: string[]) => {
    if (!showId) return;

    try {
      setLoading(true);
      const result = await showService.updateSuccessfulPromoLinks(showId, links);
      setSuccessfulLinks(result);
      toast.success('Successful promo links updated');
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update promo links';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const updatePromoPerformanceDetails = useCallback(async (details: string) => {
    if (!showId) return;

    try {
      setLoading(true);
      const result = await showService.updatePromoPerformanceDetails(showId, details);
      setPromoPerformanceDetails(result);
      toast.success('Promo performance details updated');
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update promo performance details';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);

  return {
    promos,
    successfulLinks,
    promoPerformanceDetails,
    outsourcedShow,
    loading,
    error,
    addPromo,
    updatePromo,
    deletePromo,
    updateSuccessfulLinks,
    updatePromoPerformanceDetails,
    refetch: fetchPromos
  };
};

// Hook for Dubbing Details
export const useDubbingDetails = (showId: string | null) => {
  const [projects, setProjects] = useState<DubbingProjectResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    if (!showId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await showService.getDubbingProjects(showId);
      setProjects(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch dubbing details';
      setError(errorMessage);
      console.error('Error fetching dubbing details:', err);
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const createProject = useCallback(async (projectData: Omit<DubbingProjectCreateDTO, 'showId'>) => {
    if (!showId) return;
    
    try {
      setLoading(true);
      const payload: DubbingProjectCreateDTO = {
        ...projectData,
        showId: showId
      };
      const result = await showService.createDubbingProject(showId, payload);
      setProjects(prev => [...prev, result]);
      toast.success('Dubbing project created successfully');
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create dubbing project';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const updateProject = useCallback(async (projectId: string, updateData: DubbingProjectUpdateDTO) => {
    if (!showId) return;
    
    try {
      setLoading(true);
      const result = await showService.updateDubbingProject(showId, projectId, updateData);
      setProjects(prev => prev.map(p => p.id === projectId ? result : p));
      toast.success('Dubbing project updated successfully');
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update dubbing project';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  const deleteProject = useCallback(async (projectId: string) => {
    if (!showId) return;
    
    try {
      setLoading(true);
      await showService.deleteDubbingProject(showId, projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      toast.success('Dubbing project deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete dubbing project';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return { 
    projects, 
    loading, 
    error, 
    createProject, 
    updateProject, 
    deleteProject,
    refetch: fetchProjects 
  };
};

// Combined hook for all show details
export const useShowDetails = (showId: string | null) => {
  const internalDetails = useInternalDetails(showId);
  const promoDetails = usePromoDetails(showId);
  const dubbingDetails = useDubbingDetails(showId);

  const loading = internalDetails.loading || promoDetails.loading || dubbingDetails.loading;
  const hasError = internalDetails.error || promoDetails.error || dubbingDetails.error;

  return {
    internalDetails,
    promoDetails,
    dubbingDetails,
    loading,
    hasError
  };
};